import { useContext, useState } from "react";
import Subtitle from "../../../../components/subtitle";
import Text from "../../../../components/text";
import Title from "../../../../components/title";
import { Fatura, Boleto } from "../../../../@types";
import { BsBoxArrowUpRight, BsDownload, BsFileText } from "react-icons/bs";
import Loading from "../../../../components/loading";
import ToastContext from "../../../../contexts/toast";
import { CustumerContext } from "../../../../contexts/custumer";
import APIFinanceiro from "../../../../services/APIFinanceiro";
import EmptyListMessage from "../../../../components/mensagemlistavazia";
import aux from "../../../../utils/auxiliar";

interface IProps {
  isOpen?: boolean;
  data: Fatura;
  getFile: (arquivoId: number) => Promise<{ base64?: string; errorMessage?: string }>;
  showDoc: (base64: string) => void;
}

const CardFaturaMobile: React.FC<IProps> = ({ data, getFile, showDoc }) => {
  const [open, setOpen] = useState(false);
  const [boletos, setBoletos] = useState<Boleto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { showErrorToast } = useContext(ToastContext);
  const { id_current_custumer } = useContext(CustumerContext);

  async function loadBoletos() {
    try {
      setIsLoading(true);

      const response = await APIFinanceiro.getBoletos({
        dataInicial: "",
        dataFinal: "",
        nf: parseInt(data.numeroNF),
        cliente: id_current_custumer,
      });

      if (response.boletos) {
        setBoletos(response.boletos);
      } else {
        setBoletos([]);
        showErrorToast(response.message);
      }
    } catch {
      showErrorToast("Erro ao carregar boletos");
    } finally {
      setIsLoading(false);
    }
  }

  function toggleOpen() {
    if (!open) loadBoletos();
    setOpen((old) => !old);
  }

  async function abrirBoleto(arquivoId: number) {
    const file = await getFile(arquivoId);

    if (file.base64) {
      showDoc(file.base64);
    } else if (file.errorMessage) {
      showErrorToast(file.errorMessage);
    }
  }

  async function handleBoletoDownload(boleto: Boleto) {
    const file = await getFile(Number(boleto.arquivo));

    const name = boleto.dtEmimssao;
    const splitted = name.split(" ");
    const formatted = `${splitted[0]} ${splitted[1]}`;

    if (file.base64) {
      aux.downloadFile(file.base64, formatted);

    } else if (file.errorMessage) {
      showErrorToast(file.errorMessage);
    }
  }

  return (
    <div className="w-full rounded-xl border border-gray-100 bg-white overflow-hidden shadow-sm mb-4">

      <div className="px-5 pt-3.5 pb-1 border-b border-gray-100">
        <Text className="!text-[14px] !font-bold">{data.descEmpresa}</Text>
      </div>

      <div className="px-6 pt-2.5 pb-3.5 border-b border-gray-100 flex justify-between">
        <div>
          <Subtitle className="!text-gray-400 !text-[11px]">Emissão</Subtitle>
          <Text className=" !text-[14px]">
            {data.dtEmissao.split(" ")[0]}
          </Text>
        </div>

        <div className="text-right">
          <Subtitle className="!text-gray-400 !text-[11px]">Vencimento</Subtitle>
          <Text className=" !text-[14px]">
            {data.dtVencimento}
          </Text>
        </div>
      </div>

      <div className="px-4 py-2.5 bg-[#F3F4F6] rounded-md flex justify-between items-center mx-4">
        <div>
          <Subtitle className="!text-gray-400 !text-[11px]">Bruto</Subtitle>
          <Title className="">R$ {data.valBruto}</Title>
        </div>

        <div className="text-right">
          <Subtitle className="!text-gray-400 !text-[11px]">Líquido</Subtitle>
          <Title className=" ">
            R$ {data.valorLiquido}
          </Title>
        </div>
      </div>

      <div className="px-5 pb-2.5 pt-3 flex justify-between items-center">
        {data.temBoleto ? (
          <button
            onClick={toggleOpen}
            className="flex items-center gap-1.5 text-[rgb(41,145,214)] border-0 bg-transparent cursor-pointer hover:underline"
          >
            <BsFileText size={14} />
            <Text className="!text-[rgb(41,145,214)] !text-[12px] font-semibold">
              {open ? "Fechar boletos" : "Ver boletos"}
            </Text>
          </button>
        ) : (
          <div className="flex items-center gap-1.5 text-gray-300">
            <BsFileText size={14} />
            <Text className="!text-gray-300 !text-[12px]">
              Sem boleto
            </Text>
          </div>
        )}

        <div className="flex justify-between items-center">
          {data.linkPrefeitura ? (
            <a
              href={data.linkPrefeitura}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[rgb(41,145,214)] text-[12px] font-black no-underline hover:underline"
            >
              NF: {data.numeroNF}
            </a>
          ) : (
            <Text className="!font-black !text-[12px]">
              NF: {data.numeroNF}
            </Text>
          )}
        </div>
      </div>

      {open && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
          <Subtitle className="!text-gray-400 uppercase tracking-wide !text-[11px] mb-2">
            Boletos relacionados
          </Subtitle>

          {isLoading && <Loading />}

          {!isLoading && boletos.length === 0 && (
            <EmptyListMessage texto="Não há boletos relacionados" />
          )}

          {!isLoading &&
            boletos.map((boleto) => (
              <div
                key={boleto.contasAReceber}
                className="bg-white rounded-lg border border-gray-100 px-3 py-2.5 flex justify-between items-center mb-2"
              >
                <div>
                  <Text className="!font-medium ">
                    {boleto.banco}
                  </Text>
                  <Text className="!text-gray-400 !text-[11px] mt-0.5">
                    Venc. {boleto.vencBoleto}
                  </Text>
                </div>

                <div className="flex items-center gap-3">
                  <Text className="!font-medium ">
                    R$ {boleto.valBoleto}
                  </Text>

                  <BsDownload
                    size={15}
                    className="text-[rgb(41,145,214)] cursor-pointer"
                    onClick={() => handleBoletoDownload(boleto)}
                  />

                  <BsBoxArrowUpRight
                    size={15}
                    className="text-[rgb(41,145,214)] cursor-pointer"
                    onClick={() => abrirBoleto(Number(boleto.arquivo))}
                  />
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default CardFaturaMobile;